import { ButtonType, EpicIcons } from "@constellation-academy/epic-ui";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Toast } from "primereact/toast";
import { useRef, useState, useCallback } from "react";
import { useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type createPercentageDiscountActionButton_CreateDiscountActionMutation } from "@relay/createPercentageDiscountActionButton_CreateDiscountActionMutation.graphql";
import { CREATE_DISCOUNT_ACTION_MUTATION } from "./create-percentage-discount-action-button.graphql";
import { type CreatePercentageDiscountActionButtonProps } from "./create-percentage-discount-action-button.types";
import { Button } from "../../../../components/button";

export const CreatePercentageDiscountActionButton = ({
	connectionId,
}: CreatePercentageDiscountActionButtonProps) => {
	const { t } = useTranslation(["common", "validation"]);

	const [createDiscountAction, isCreatingDiscountAction] =
		useMutation<createPercentageDiscountActionButton_CreateDiscountActionMutation>(
			CREATE_DISCOUNT_ACTION_MUTATION,
		);

	const overlayPanel = useRef<OverlayPanel>(null);
	const toast = useRef<Toast>(null);

	const [title, setTitle] = useState("");
	const [titleError, setTitleError] = useState<string | null>(null);

	const validateTitle = (value: string): string | null => {
		if (!value.trim()) {
			return t("validation:required");
		}
		return null;
	};

	const handleTitleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newValue = e.target.value;
			setTitle(newValue);
			if (titleError) {
				setTitleError(null);
			}
		},
		[titleError],
	);

	const handleOnClick = () => {
		const error = validateTitle(title);

		if (error) {
			setTitleError(error);
			return;
		}

		overlayPanel.current?.hide();

		createDiscountAction({
			variables: {
				input: {
					title: title.trim(),
				},
				connections: [connectionId],
			},
			onCompleted: () => {
				setTitle("");
				setTitleError(null);

				toast.current?.show({
					severity: "success",
					life: 3000,
					summary: t("common:discounts.actionCreated"),
					detail: t("common:discounts.actionCreatedSuccessfully"),
				});
			},
		});
	};

	return (
		<>
			<Toast ref={toast} />
			<Button
				type={ButtonType.Button}
				label={t("common:discounts.newPercentageAction")}
				icon={EpicIcons.PLUS}
				loading={isCreatingDiscountAction}
				disabled={isCreatingDiscountAction}
				onClick={(e) => overlayPanel.current?.toggle(e)}
			/>
			<OverlayPanel ref={overlayPanel} className="p-overlaypanel-content-no-padding">
				<div className="p-fluid">
					<span className="p-float-label mb-2 mt-3">
						<InputText
							id="nameOfAction"
							value={title}
							onChange={handleTitleChange}
							required
							className={titleError ? "p-invalid" : ""}
						/>
						<label htmlFor="nameOfAction">{t("common:discounts.actionTitle")}</label>
					</span>
					{titleError && <small className="p-error block">{titleError}</small>}
					<Button
						type={ButtonType.Button}
						label={t("common:discounts.createAction")}
						loading={isCreatingDiscountAction}
						disabled={
							isCreatingDiscountAction || isCreatingDiscountAction || !!titleError
						}
						onClick={handleOnClick}
					/>
				</div>
			</OverlayPanel>
		</>
	);
};
